/**
 * Data Quality — system + per-bank missingness (PHASE2_SPEC §4.6).
 *
 * Layout:
 *   3 KPI tiles (system avg loan PAN miss · loan mobile miss · deposit nominee miss)
 *   Sortable per-bank table with three % columns + worst-flag pill.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { dqFlag } from '../../helpers/flag';
import { formatPct } from '../../helpers/format';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchDataQuality, selectDataQuality } from '../../redux/slices/dashboard';
import type { DataQualityByBankRow } from '../../type/api';

export default function DataQualityPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectDataQuality);

	const [search, setSearch] = useState('');

	useEffect(() => {
		if (cell.status === 'idle') {
			dispatch(fetchDataQuality());
		}
	}, [cell.status, dispatch]);

	const data = cell.data;
	const byBank: DataQualityByBankRow[] = useMemo(
		() => (Array.isArray(data?.byBank) ? data!.byBank : []),
		[data],
	);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return byBank;
		return byBank.filter(
			(b) => b.orgName.toLowerCase().includes(q) || String(b.orgId).includes(q),
		);
	}, [byBank, search]);

	const sort = useSortable<DataQualityByBankRow>(filtered, {
		field: 'loanMissingPanPct',
		dir: 'desc',
	});

	const handleRetry = () => dispatch(fetchDataQuality());

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Data Quality</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={5} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Data Quality</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load data quality'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Data Quality</h1></div></div>
				<Card><EmptyState title="No data quality data" /></Card>
			</div>
		);
	}

	const sys = data.system;

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Data Quality</h1>
					<div className="page-sub">
						System-wide missing-field percentages.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={handleRetry}>Refresh</button>
			</div>

			<div className="grid cols-3">
				<KpiTile
					label="Loan PAN Missing"
					value={formatPct(sys.loanMissingPanPct)}
					flag={dqFlag(sys.loanMissingPanPct)}
					flagVariant="dq"
					primary
				/>
				<KpiTile
					label="Loan Mobile Missing"
					value={formatPct(sys.loanMissingMobilePct)}
					flag={dqFlag(sys.loanMissingMobilePct)}
					flagVariant="dq"
				/>
				<KpiTile
					label="Deposit Nominee Missing"
					value={formatPct(sys.depositMissingNomineePct)}
					flag={dqFlag(sys.depositMissingNomineePct)}
					flagVariant="dq"
				/>
			</div>

			<Card title="Filters">
				<div className="filterbar">
					<div className="search">
						<input
							type="search"
							value={search}
							placeholder="Search bank…"
							onChange={(e) => setSearch(e.target.value)}
							style={{ width: 300 }}
						/>
					</div>
					<div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-faint)' }}>
						{filtered.length} banks
					</div>
				</div>
			</Card>

			<Card title="Per-bank data quality">
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={sort} numeric>ID</SortableTh>
								<SortableTh field="orgName" sort={sort}>Bank</SortableTh>
								<SortableTh field="loanMissingPanPct" sort={sort} numeric>Loan PAN missing</SortableTh>
								<SortableTh field="loanMissingMobilePct" sort={sort} numeric>Loan Mobile missing</SortableTh>
								<SortableTh field="depositMissingNomineePct" sort={sort} numeric>Dep. Nominee missing</SortableTh>
								<SortableTh field="worstFlag" sort={sort}>Worst</SortableTh>
							</tr>
						</thead>
						<tbody>
							{sort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.orgId}</td>
									<td><BankBadge orgId={b.orgId} orgName={b.orgName} /></td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={dqFlag(b.loanMissingPanPct)} variant="dq" compact label={formatPct(b.loanMissingPanPct)} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={dqFlag(b.loanMissingMobilePct)} variant="dq" compact label={formatPct(b.loanMissingMobilePct)} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={dqFlag(b.depositMissingNomineePct)} variant="dq" compact label={formatPct(b.depositMissingNomineePct)} />
									</td>
									<td><StatusPill flag={b.worstFlag} variant="dq" /></td>
								</tr>
							))}
							{!sort.rows.length && (
								<tr><td colSpan={6}><EmptyState title="No banks match" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>
		</div>
	);
}
